import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store.js';
import {
  LayoutDashboard, ClipboardList, BarChart3, Users, Building2, Settings,
  CreditCard, ScrollText, LogOut, ShieldCheck
} from 'lucide-react';
import clsx from 'clsx';

export default function Layout() {
  const { user, company, logout, isSuperUser, isGroupAdmin } = useAuthStore();
  const navigate = useNavigate();

  if (!user) return null;

  const su = isSuperUser();
  const ga = isGroupAdmin();

  const sections = [
    {
      label: 'You',
      items: [
        { to: '/dashboard', label: 'My dashboard', icon: LayoutDashboard },
        { to: '/assessments', label: 'My assessments', icon: ClipboardList }
      ]
    },
    (su || ga) && {
      label: 'Insights',
      items: [
        su && { to: '/insights/company', label: 'Company', icon: Building2 },
        (su || ga) && { to: '/insights/groups', label: 'Groups', icon: BarChart3 },
        (su || ga) && { to: '/insights/individuals', label: 'Individuals', icon: Users }
      ].filter(Boolean)
    },
    su && {
      label: 'Setup',
      items: [
        { to: '/setup/organization', label: 'Organisation', icon: Building2 },
        { to: '/setup/users', label: 'Users', icon: Users },
        { to: '/setup/permissions', label: 'Permissions', icon: ShieldCheck },
        { to: '/billing', label: 'Billing', icon: CreditCard },
        { to: '/audit-log', label: 'Audit log', icon: ScrollText },
        { to: '/settings', label: 'Settings', icon: Settings }
      ]
    }
  ].filter(Boolean);

  return (
    <div className="min-h-screen flex bg-ink-50">
      <aside className="w-60 shrink-0 border-r border-ink-200/70 bg-white flex flex-col">
        <Link to="/dashboard" className="px-5 py-5 flex items-center gap-2 border-b border-ink-200/70">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-700 to-accent-600 grid place-items-center text-white font-bold">S</div>
          <div className="flex-1">
            <div className="font-semibold text-ink-900 leading-tight">Skillbase</div>
            <div className="text-[11px] text-ink-500 truncate">{company?.name}</div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
          {sections.map((section, i) => (
            <div key={i}>
              <div className="px-2 text-[11px] uppercase tracking-wider font-semibold text-ink-400 mb-1.5">{section.label}</div>
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <NavLink key={item.to} to={item.to}
                    className={({ isActive }) => clsx(
                      'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors',
                      isActive ? 'bg-primary-50 text-primary-800' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                    )}>
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-ink-200/70">
          <div className="px-2 mb-2">
            <div className="text-sm font-medium text-ink-900 truncate">{user.full_name}</div>
            <div className="text-[11px] text-ink-500 truncate">{user.email}</div>
          </div>
          <button onClick={async () => { await logout(); navigate('/login'); }}
            className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-sm text-ink-600 hover:bg-ink-100 hover:text-ink-900">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="bg-white border-b border-ink-200/70 px-8 py-3 flex items-center justify-between">
          <div className="text-xs text-ink-500">Australian data residency · {company?.subscription_status === 'sandbox' ? 'Demo sandbox' : `${company?.subscription_status || 'trialing'} plan`}</div>
          {company?.subscription_status === 'trialing' && company?.trial_ends_at ? (
            <div className="text-xs text-ink-600">Trial ends {new Date(company.trial_ends_at).toLocaleDateString('en-AU')}</div>
          ) : null}
        </div>
        <div className="px-8 py-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
